<template>
	<header>
		<nav class="main fix-on-scroll" :class="page && page.headingImage ? '' : 'no-slider'">
			<div class="row" :class="{ activenav: showMenu }">
				<div class="columns column12 flex-row">
					<div v-if="showMenu" class="menu-button button" @click="hideMenu">
						<font-awesome-icon icon="fa-regular fa-times" size="1x" />
						<span>{{ $t('close') }}</span>
					</div>
					<div v-else class="menu-button button" @click="toggleMenu">
						<font-awesome-icon icon="fa-regular fa-bars" size="1x" />
						<span>{{ $t('menu') }}</span>
					</div>
					<nuxt-link class="logo" aria-label="Back to homepage" :to="localePath('/')" @click="hideMenu" />
					<div v-click-outside="closeLangNav" class="nav-buttons">
						<lang-select :items="page.langmenu" />
						<a :href="`mailto:${defaults[locale].website.email}`">
							<font-awesome-icon icon="fa-light fa-envelope" size="1x" />
						</a>
						<a :href="`tel:${$phoneRaw(defaults[locale].website.phone)}`">
							<font-awesome-icon icon="fa-light fa-phone" size="1x" />
						</a>
						<book-button class="button button-cta reserve-now right" aria-label="Reserve now">
							<font-awesome-icon icon="fa-light fa-calendar-days" size="1x" />
							<span>{{ $t('bookNow') }}</span>
						</book-button>
					</div>
				</div>
			</div>
			<div class="nav-container" :class="{ active: showMenu }">
				<div class="row">
					<ul class="main-nav">
						<li
							v-for="item in topmenu[locale]"
							:key="item.filename"
							:class="{ 'has-dropdown': item.submenu.length }"
						>
							<div class="nav-item">
								<span>
									<nuxt-link
										class="nav-link"
										:exact="item.slug === 'index'"
										:to="`${item.filename}`"
										@click="hideMenu"
									>
										{{ item.header }}
									</nuxt-link>
									<submenu
										v-if="item.submenu.length"
										:hide-all="!showMenu"
										:item="item"
										:subitems="item.submenu"
										@hide-menu="hideMenu"
									/>
								</span>
								<nuxt-link
									class="nav-arrow-right"
									:exact="item.slug === 'index'"
									:to="`${item.filename}`"
									@click="hideMenu"
								>
									<font-awesome-icon icon="fa-light fa-arrow-right" size="1x" />
								</nuxt-link>
							</div>
						</li>
						<li v-if="page && page.langmenu.length" class="lang-nav lang-nav-mobile">
							<div v-for="item in page.langmenu" :key="`${item.filename}-${item.language}`">
								<nuxt-link
									class="flag"
									:class="item.language"
									:to="`${item.filename}`"
									@click="hideMenu"
								>
									<span>{{ $t(item.language) }}</span>
								</nuxt-link>
							</div>
						</li>
					</ul>
					<div class="nav-contact">
						<a :href="`tel:${$phoneRaw(defaults[locale].website.phone)}`">
							<font-awesome-icon icon="fa-light fa-phone" size="1x" />
							<span>{{ defaults[locale].website.phone }}</span>
						</a>
						<social-media :socials="socials" />
						<a :href="`mailto:${defaults[locale].website.email}`">
							<font-awesome-icon icon="fa-light fa-envelope" size="1x" />
							<span>{{ defaults[locale].website.email }}</span>
						</a>
					</div>
				</div>
			</div>
		</nav>
	</header>
</template>

<script setup>
const { page, fetchPage } = usePage();
const { defaults, socials, fetchDefaults } = useDefaults();
const { locale } = useI18n();
const localePath = useLocalePath();

await fetchPage();
await fetchDefaults();

const showLangNav = ref(false);
const showMenu = ref(false);

const { data: topmenu } = await useWebsiteFetch('topmenu');

const closeLangNav = () => {
	showLangNav.value = false;
};

const toggleMenu = () => {
	showMenu.value = !showMenu.value;
	document.body.classList.add('locked');
};

const hideMenu = () => {
	showMenu.value = false;
	document.body.classList.remove('locked');
};
</script>

<style lang="scss" scoped>
.logo {
	background: url('~/assets/images/logo-hotelkint-full.png') no-repeat center center;
	background-size: 149px;
	width: 149px;
	height: 86px;
	position: absolute;
	left: 0;
	right: 0;
	margin: 0 auto;
	animation: fade-from-top 1s ease;
	transition: all 0.3s ease-in-out;
}

nav.main {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	z-index: 101;
	padding: 40px 0;
	transition: all 0.3s ease-in-out;
	overflow: visible;
}

nav .row {
	overflow: visible;
	z-index: 102;
	position: relative;
}

nav.main.scroll,
nav.main.no-slider {
	padding: 24px;
	background: #000;

	.logo {
		background: url('~/assets/images/logo-hotelkint-smaller.png') no-repeat center center;
		background-size: 130px;
		width: 130px;
		height: 75px;
	}
}

.reserve-now {
	display: flex;
	flex-flow: row nowrap;
	justify-content: center;
	align-items: center;
	padding: 10px 25px;
}

.activenav .reserve-now {
	color: #fff;
	border: 1px solid #fff;

	&:hover {
		color: var(--cta-color);
		background: #fff;
	}
}

.menu-button {
	background: none;
	padding: 0;
	margin: 0;
	font-size: 16px;
	letter-spacing: 1px;
	font-weight: 400;
	cursor: pointer;
	text-align: left;
	animation: fade-from-left 0.7s ease;
	display: flex;
	flex-flow: row nowrap;
	justify-content: space-between;
	align-items: center;
	gap: 10px;
	opacity: 0.85;

	svg {
		font-size: 26px;
	}

	&:hover {
		opacity: 1;
		background: none;
	}
}

nav .flex-row,
.nav-buttons {
	justify-content: space-between;
	display: flex;
	flex-wrap: wrap;
	align-items: center;
}

.nav-buttons {
	gap: 22px;
	color: #fff;
	animation: fade-from-right 0.7s ease;

	a {
		color: #fff;
		text-decoration: none;
		margin: 0;
		opacity: 0.85;

		&.button,
		&:hover {
			opacity: 1;
		}
	}

	.button-cta svg {
		display: none;
	}
}

.reserve-now i {
	display: none;
}

.nav-container {
	background: #d68d0e;
	padding: 30px 50px;
	position: fixed;
	top: 0;
	left: 0;
	padding-top: 80px;
	z-index: 101;
	width: 100%;
	height: 100%;
	opacity: 0;
	transition: all 0.4s ease-in-out;
	transform: translate(0, 100%) matrix(1, 0, 0, 1, 0, 0);

	.row {
		display: flex;
		flex-flow: column nowrap;
		justify-content: space-between;
		max-width: 800px;
		text-align: left;
		align-items: flex-start;
		height: 100%;
	}
}

.nav-container.active {
	transform: matrix(1, 0, 0, 1, 0, 0);
	opacity: 1;
}

.lang-nav {
	display: none;
	list-style: none;
	text-align: left;
	float: left;
	width: 50%;
	line-height: 26px;
	padding-top: 40px;

	a {
		color: #fff;
		display: flex;
		flex-flow: row nowrap;
		align-items: center;
		justify-content: flex-start;
		font-size: 16px !important;
		gap: 5px;

		&.router-link-exact-active,
		&:hover {
			color: var(--cta-color);
		}
	}

	img {
		max-width: 16px;
		float: left;
		margin: 0 5px 0 0;
	}
}

.lang-select {
	padding: 12px 15px;
	border-right: 1px solid var(--primary-background-color);
	cursor: pointer;
	position: relative;
	text-transform: uppercase;

	i {
		font-size: 12px;
	}
}

.lang-wrapper {
	position: absolute;
	top: 35px;
	left: 3px;
	right: 3px;
	padding: 10px 9px;
	text-align: center;
	background: #fff;
	box-shadow: 0 18px 18px rgb(0 0 0 / 10%);
	border-radius: 3px;
	z-index: 90;
}

.lang-wrapper.active,
.lang-wrapper a {
	display: flex;
	flex-flow: column nowrap;
	place-content: space-between flex-start;
	color: #000;
	gap: 5px;
}

.lang-wrapper a {
	width: 100%;
	flex-direction: row;
	font-size: 14px;
	margin: 2px 0;
}

.lang-wrapper img {
	width: 16px;
	height: 16px;
	margin: 2px 0 0;
}

.mobile-lang-nav {
	margin: 40px 0 0;
	font-size: 14px;
	display: none;
}

.mobile-lang-nav img {
	max-width: 16px;
	margin: 0 10px 0 0;
}

.main-nav .mobile-lang-nav a {
	width: 100%;
	display: block;
	font-size: 16px;
	line-height: 28px;
}

/* Fullscreen Nav */
.main-nav {
	list-style: none;
	min-width: 25%;
	width: 100%;
	text-align: left;
	padding-top: 100px;

	.nav-item {
		border-bottom: 1px solid #f6efd3;
		padding: 10px 0;
		display: flex;
		flex-flow: row nowrap;
		align-items: center;
		color: #fff;

		svg {
			font-size: 36px;
			transform: rotate(315deg);
			margin-left: auto;
		}
	}

	a {
		color: #fff;
		font-size: 32px;
		line-height: 54px;
		font-weight: 400;
		text-transform: uppercase;

		&:hover,
		&.active {
			color: #e1d5a6;
		}
	}

	.nav-link {
		width: 100%;
	}

	.nav-arrow-right {
		margin-left: auto;
	}
}

.nav-contact {
	align-self: flex-end;
	width: 100%;
	display: flex;
	flex-flow: row wrap;
	align-items: center;
	text-align: left;
	justify-content: flex-start;
	gap: 20px;
	border-top: 1px solid rgb(255 255 255 / 30%);
	padding: 20px 0 0;

	a {
		svg {
			margin: 0 10px 0 0;
		}
	}

	.socials {
		margin: 0;

		:deep(a:hover) {
			color: #f6efd3;
		}
	}
}

.nav-contact > a {
	color: #fff;
	display: block;
	font-weight: 300;
	margin: 0;
}

.nav-contact > a i {
	margin: 0 10px 0 0;
}

.close-wrapper {
	align-self: flex-start;
}

.nav-close {
	color: #fff;
	cursor: pointer;
	font-size: 54px;
	padding: 0 0 0 12px;
}

/* Breadcrumbs */
.breadcrumbs ul {
	list-style: none;
	display: flex;
	flex-wrap: wrap;
	justify-content: flex-start;
	align-items: center;
	margin: 0 0 75px;
}

.breadcrumbs li {
	margin: 0 10px 0 0;
}

.breadcrumbs i {
	font-size: 11px;
}

.breadcrumbs a {
	color: var(--body-color);
}

.breadcrumbs a.active {
	color: var(--cta-color);
}

@media (max-height: 740px) {
	.nav-container {
		overflow-y: scroll;
	}

	.lang-nav {
		position: relative;
		bottom: auto;
		left: auto;
		z-index: 1;
	}

	.main-nav {
		z-index: 2;
		position: relative;
	}
}

@media (max-width: 920px) {
	.reserve-now {
		padding: 0;

		svg {
			margin: 0;
		}
	}

	.menu-button {
		justify-content: center;
	}

	.lang-nav {
		display: block;
	}

	.close-wrapper {
		align-self: flex-end;
	}

	.nav-buttons svg,
	.nav-buttons .button-cta svg {
		display: unset;
	}

	.main-nav .lang-nav {
		display: flex;
		flex-flow: row wrap;
		justify-content: flex-start;
		align-items: center;

		a {
			display: flex;
			flex-flow: row nowrap;
			justify-content: flex-start;
			align-items: center;
			color: #fff;
			width: auto;
			margin: 0 25px 0 0;
			text-transform: none;
			padding: 0;

			&::before {
				top: 0;
			}
		}
	}
}

@media (max-width: 800px) {
	.lang-nav {
		position: relative;
		bottom: auto;
		left: auto;
		right: auto;
		float: left;
		margin-top: 30px;
	}

	.main-nav {
		.nav-link {
			font-size: 22px;
			line-height: 44px;
		}
	}

	.nav-buttons {
		.lang-select {
			display: none;
		}
	}
}

@media (max-width: 600px) {
	.nav-container {
		padding: 10px 25px 25px;
	}

	.main-nav,
	.contact-info,
	.lang-nav {
		width: 100%;
	}

	nav.main {
		.flex-row {
			padding: 0;
		}
	}

	nav.main.scroll,
	nav.main.no-slider {
		padding: 18px 6px;
	}

	.contact-info {
		margin: 25px auto 0;
	}

	.lang-nav {
		margin-top: 0;
	}

	nav .buttons .button {
		svg {
			margin-right: 0;
			font-size: 16px;
		}

		span {
			display: none;
		}
	}
}
</style>
